import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { convertMoneyToMinorUnits } from '@theme/money-formatting';

window.__CART_BUILD__ = 'CART-2026-09-13-A';
console.info(
  '[CART BUILD]',
  window.__CART_BUILD__,
  typeof import.meta !== 'undefined' && import.meta.url ? import.meta.url : document.currentScript?.src || ''
);

/**
 * CartCore — only cart authority.
 *
 * Drawer nodes replaced on each render:
 * 1. cart-items-component children (header + lines + summary)
 * 2. [data-testid="cart-total-value"] (explicit replaceWith from fresh HTML)
 * 3. cart-icon count via applyItemCount
 * 4. dialog.cart-drawer--empty + aria-labelledby
 *
 * No morph. No CartAddEvent. No startViewTransition on empty.
 */

const DRAWER_SECTION = 'ajax-cart-drawer';

function jsRoute(url) {
  if (!url) return url;
  return url.endsWith('.js') ? url : `${url}.js`;
}

function parseMoney(text, currency) {
  return convertMoneyToMinorUnits(String(text || '').trim(), currency || 'EUR');
}

class CartCoreImpl {
  #queue = Promise.resolve();
  /** @type {object | null} */
  #cart = null;
  #renderGen = 0;

  /**
   * @template T
   * @param {() => Promise<T>} operation
   */
  enqueue(operation) {
    const run = this.#queue.then(operation, operation);
    this.#queue = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  async getState() {
    if (this.#cart) return this.#cart;
    return this.fetchCart();
  }

  async fetchCart() {
    this.#cart = await (await fetch('/cart.js', { cache: 'no-store' })).json();
    return this.#cart;
  }

  addVariant(variantId, quantity = 1) {
    return this.enqueue(async () => {
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', String(quantity));
      this.#appendSections(formData);
      const mutation = await this.#postAdd(formData);
      if (mutation.status) {
        throw new Error(mutation.description || mutation.message || 'Cart add failed');
      }
      return this.#pipeline(mutation, 'add');
    });
  }

  addFormData(formData) {
    return this.enqueue(async () => {
      this.#appendSections(formData);
      const mutation = await this.#postAdd(formData);
      if (mutation.status) {
        return { error: mutation, cart: null };
      }
      const result = await this.#pipeline(mutation, 'add');
      return { error: null, ...result };
    });
  }

  remove(key) {
    return this.setQuantity(key, 0);
  }

  setQuantity(key, quantity) {
    return this.enqueue(async () => {
      const mutation = await this.#postChange({ id: key, quantity });
      if (mutation.errors) {
        const error = new Error(typeof mutation.errors === 'string' ? mutation.errors : 'Cart change failed');
        error.cartError = mutation;
        throw error;
      }
      return this.#pipeline(mutation, quantity === 0 ? 'remove' : 'quantity');
    });
  }

  applyUpdate(payload, options = {}) {
    const { render = true, extraSections = [] } = options;
    return this.enqueue(async () => {
      const sections = [DRAWER_SECTION, ...extraSections].filter(Boolean).join(',');
      const body = JSON.stringify({
        ...payload,
        ...(render || extraSections.length
          ? { sections, sections_url: window.location.pathname || '/' }
          : {}),
      });
      const mutation = await (
        await fetch(jsRoute(Theme.routes.cart_update_url), fetchConfig('json', { body }))
      ).json();
      if (!render) {
        this.#cart = await this.fetchCart();
        return { cart: this.#cart, mutation };
      }
      const result = await this.#pipeline(mutation, 'update');
      return { ...result, mutation };
    });
  }

  refresh() {
    return this.enqueue(async () => {
      const cart = await this.fetchCart();
      if (Number(cart.item_count) === 0) {
        this.render(cart, {}, 'refresh');
        return { cart };
      }
      const html = await this.#fetchDrawerHtml();
      this.render(cart, { [DRAWER_SECTION]: html }, 'refresh');
      return { cart };
    });
  }

  render(cart, sections = {}, source = 'render') {
    this.#cart = cart;
    const renderGen = ++this.#renderGen;
    const html = typeof sections[DRAWER_SECTION] === 'string' ? sections[DRAWER_SECTION] : '';

    this.#renderBadge(cart);
    this.#renderDrawer(cart, html, renderGen);

    const state = this.verify();
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.cartChanged, {
        bubbles: true,
        detail: { cart, source },
      })
    );
    return state;
  }

  verify() {
    const state = this.debugState();
    if (!state.ok) {
      console.error('[CART VERIFY FAIL]', state);
    }
    return state;
  }

  debugState() {
    if (!this.#cart) {
      return {
        item_count: null,
        badge: null,
        drawerQty: null,
        total_price: null,
        domTotal: null,
        domTotalText: '',
        empty: null,
        ok: false,
        reason: 'cart not hydrated',
      };
    }

    const cart = this.#cart;
    const shopifyItemCount = Number(cart.item_count) || 0;
    const shopifyTotal = Number(cart.total_price) || 0;

    const badgeEl = document.querySelector('[ref="cartBubbleCount"]');
    const badgeHidden = badgeEl?.classList.contains('hidden');
    const badgeText = (badgeEl?.textContent || '').trim();
    const badgeCount = badgeHidden || badgeText === '' ? 0 : Number(badgeText);

    const drawer = document.querySelector('cart-items-component');
    const hasForm = Boolean(drawer?.querySelector('.cart-form'));
    const emptyHeading = drawer?.querySelector('#cart-drawer-heading-empty');
    const emptyState = Boolean(emptyHeading) && !hasForm;

    const inputs = emptyState
      ? []
      : [...(drawer?.querySelectorAll('tbody input[data-cart-line], tbody [ref="quantityInput"]') || [])];
    const drawerQty = inputs.reduce((sum, input) => sum + (Number(input.value) || 0), 0);

    const totalEl = drawer?.querySelector('[data-testid="cart-total-value"]');
    const domTotalText = (totalEl?.getAttribute('value') || totalEl?.textContent || '').replace(/\s+/g, ' ').trim();
    const hasStaleTotal = emptyState && Boolean(totalEl && domTotalText);
    const domTotal = emptyState ? 0 : parseMoney(domTotalText || '0', cart.currency);

    let ok = false;
    if (shopifyItemCount === 0) {
      ok = badgeCount === 0 && drawerQty === 0 && emptyState && !hasStaleTotal && !hasForm;
    } else {
      ok =
        badgeCount === shopifyItemCount &&
        drawerQty === shopifyItemCount &&
        emptyState === false &&
        hasForm &&
        domTotal === shopifyTotal;
    }

    return {
      item_count: shopifyItemCount,
      badge: badgeCount,
      drawerQty,
      total_price: shopifyTotal,
      domTotal,
      domTotalText,
      empty: emptyState,
      ok,
    };
  }

  async #pipeline(mutation, source) {
    const cart = await this.fetchCart();
    if (Number(cart.item_count) === 0) {
      this.render(cart, {}, source);
      return { cart, sections: {} };
    }

    let html = mutation?.sections?.[DRAWER_SECTION] || '';
    if (!this.#htmlMatchesCart(html, cart)) {
      const gen = this.#renderGen;
      html = await this.#fetchDrawerHtml();
      if (gen !== this.#renderGen) {
        return { cart, sections: { [DRAWER_SECTION]: html }, skipped: true };
      }
    }
    this.render(cart, { [DRAWER_SECTION]: html }, source);
    return { cart, sections: { [DRAWER_SECTION]: html } };
  }

  #appendSections(formData) {
    formData.delete('sections');
    formData.delete('sections_url');
    formData.append('sections', DRAWER_SECTION);
    formData.append('sections_url', window.location.pathname || '/');
  }

  async #postAdd(formData) {
    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    });
    return response.json();
  }

  async #postChange(payload) {
    const body = JSON.stringify({
      ...payload,
      sections: DRAWER_SECTION,
      sections_url: window.location.pathname || '/',
    });
    return (await fetch(jsRoute(Theme.routes.cart_change_url), fetchConfig('json', { body }))).json();
  }

  async #fetchDrawerHtml() {
    const body = JSON.stringify({
      sections: DRAWER_SECTION,
      sections_url: window.location.pathname || '/',
    });
    const data = await (
      await fetch(jsRoute(Theme.routes.cart_update_url), fetchConfig('json', { body }))
    ).json();
    return data.sections?.[DRAWER_SECTION] || '';
  }

  #htmlMatchesCart(html, cart) {
    const itemCount = Number(cart.item_count) || 0;
    if (itemCount === 0) return true;
    if (!html || !html.includes('cart-form')) return false;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector('cart-items-component');
    if (!root) return false;

    const inputs = root.querySelectorAll('tbody input[data-cart-line], tbody [ref="quantityInput"]');
    const qty = inputs.length
      ? [...inputs].reduce((sum, input) => sum + (Number(input.value) || 0), 0)
      : root.querySelectorAll('tbody .cart-items__table-row:not(.cart-items__nested-line)').length;
    if (qty !== itemCount) return false;

    const totalNode = root.querySelector('[data-testid="cart-total-value"]');
    const htmlTotal = parseMoney(totalNode?.getAttribute('value') || totalNode?.textContent || '', cart.currency);
    if (htmlTotal == null) return false;
    return htmlTotal === Number(cart.total_price);
  }

  #renderBadge(cart) {
    const itemCount = Number(cart.item_count) || 0;
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.applyItemCount === 'function') icon.applyItemCount(itemCount, itemCount > 0);
    });
  }

  #renderDrawer(cart, html, renderGen) {
    const itemCount = Number(cart.item_count) || 0;
    document.querySelectorAll('cart-items-component').forEach((root) => {
      if (renderGen !== this.#renderGen) return;

      if (itemCount === 0) {
        this.#showEmpty(root);
        return;
      }

      const fresh = this.#parseCartItems(html);
      if (!fresh?.querySelector('.cart-form')) {
        console.error('[CART VERIFY FAIL]', { reason: 'missing cart-form HTML', itemCount });
        return;
      }

      const freshTotal = fresh.querySelector('[data-testid="cart-total-value"]');
      root.replaceChildren(...fresh.cloneNode(true).childNodes);

      const liveTotal = root.querySelector('[data-testid="cart-total-value"]');
      if (liveTotal && freshTotal) {
        liveTotal.replaceWith(freshTotal.cloneNode(true));
      }

      if (typeof root.updatedCallback === 'function') root.updatedCallback();
      this.#setEmptyClass(root, false);
      this.#settleDrawer(root);
    });
  }

  #parseCartItems(html) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (
      doc.querySelector('cart-items-component[data-drawer]') ||
      doc.querySelector('cart-items-component')
    );
  }

  #showEmpty(root) {
    const template = document.getElementById('empty-cart-template');
    document.getElementById('cart-drawer-heading')?.remove();
    root.querySelector('[data-testid="cart-total-value"]')?.remove();
    if (template instanceof HTMLTemplateElement) {
      root.replaceChildren(document.importNode(template.content, true));
    } else {
      root.replaceChildren();
    }
    if (typeof root.updatedCallback === 'function') root.updatedCallback();
    this.#setEmptyClass(root, true);
    this.#settleDrawer(root);
  }

  #setEmptyClass(root, isEmpty) {
    const dialog = root.closest('dialog') || document.querySelector('.cart-drawer__dialog');
    if (!dialog) return;
    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
  }

  #settleDrawer(root) {
    const dialog = root.closest('dialog') || document.querySelector('.cart-drawer__dialog');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    dialog.classList.remove('dialog-closing');
    dialog.style.animation = 'none';
    dialog.style.transform = 'none';
  }
}

export const CartCore = new CartCoreImpl();
window.CartCore = CartCore;
